/**
 * Custom useMediaQuery to account for SSR as
 * chakra's version doesn't load the correct state initially
 */
import { useCallback, useEffect, useLayoutEffect, useState } from "react";

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

/**
 * @deprecated This is marked deprecated to limit and discourage the use of useMediaQuery as it affects performance.
 * Use CSS where possible so that the responsibilities pertaining to media queries can be
 * handled by the CSS engine of the browser, removing JS from the equation.
 * Do not use useMediaQuery from `@chakra/react` as it is not SSR-safe
 */
const useMediaQuery = (query: string): boolean[] => {
  const [targetReached, setTargetReached] = useState<boolean>(false);

  const updateTarget = useCallback((e: MediaQueryListEvent) => {
    if (e.matches) {
      setTargetReached(true);
    } else {
      setTargetReached(false);
    }
  }, []);

  useIsomorphicLayoutEffect(() => {
    const media = window.matchMedia(query);
    media.addListener(updateTarget);

    // Check on mount (callback is not called until a change occurs)
    if (media.matches) {
      setTargetReached(true);
    }

    return () => media.removeListener(updateTarget);
  }, []);

  return [targetReached];
};

export default useMediaQuery;
